// src/components/FactoryBarcodeSearch.jsx

import React, { useState } from "react";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  startAt,
  endAt,
  limit,
  getDocs,
} from "firebase/firestore";

const SEARCH_LIMIT = 50;

export default function FactoryBarcodeSearch() {
  const [field, setField] = useState("BarcodeNumber");
  const [search, setSearch] = useState("");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const formatDate = (timestamp) => {
    if (!timestamp?.seconds) return "-";
    return new Date(timestamp.seconds * 1000).toLocaleString();
  };

  const yesNo = (value) => (value ? "Yes" : "No");

  const runSearch = async () => {
    const term = search.trim();

    if (!term) {
      alert("Enter something to search.");
      return;
    }

    setLoading(true);
    setRows([]);

    try {
      const db = getFirestore();
      const parentRef = collection(db, "factorybarcode");

      let parentQuery;

      if (field === "BarcodeNumber") {
        // Best case: BarcodeNumber is also the document ID / stored field
        parentQuery = query(
          parentRef,
          orderBy("BarcodeNumber"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(SEARCH_LIMIT)
        );
      } else if (field === "SerialNumber") {
        parentQuery = query(
          parentRef,
          orderBy("SerialNumber"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(SEARCH_LIMIT)
        );
      } else if (field === "ProductCode") {
        parentQuery = query(
          parentRef,
          orderBy("ProductCode"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(SEARCH_LIMIT)
        );
      } else if (field === "Program") {
        parentQuery = query(
          parentRef,
          orderBy("Program"),
          startAt(term),
          endAt(term + "\uf8ff"),
          limit(SEARCH_LIMIT)
        );
      } else {
        throw new Error("Unsupported search field");
      }

      const parentSnap = await getDocs(parentQuery);

      const results = [];

      for (const parentDoc of parentSnap.docs) {
        const parentData = parentDoc.data();

        const instancesRef = collection(
          db,
          "factorybarcode",
          parentDoc.id,
          "instances"
        );

        const instancesQuery = query(
          instancesRef,
          orderBy("PrintedAt", "desc"),
          limit(100)
        );

        const instancesSnap = await getDocs(instancesQuery);

        instancesSnap.forEach((instanceDoc) => {
          const instanceData = instanceDoc.data();

          results.push({
            id: instanceDoc.id,
            parentId: parentDoc.id,

            BarcodeNumber: parentData.BarcodeNumber || parentDoc.id || "",
            SerialNumber: parentData.SerialNumber || "",
            ProductCode: parentData.ProductCode || "",
            Program: parentData.Program || "",
            Quantity: parentData.Quantity ?? "",

            CreatedAt: parentData.CreatedAt || null,

            PrintedAt: instanceData.PrintedAt || null,
            Printed: instanceData.Printed || false,

            stretch_scan: instanceData.stretch_scan || false,
            stretch_scantime: instanceData.stretch_scantime || null,

            despatch_scan: instanceData.despatch_scan || false,
            despatch_scantime: instanceData.despatch_scantime || null,

            hortonwood_scan: instanceData.hortonwood_scan || false,
            hortonwood_scantime: instanceData.hortonwood_scantime || null,
          });
        });
      }

      setRows(results);
    } catch (err) {
      console.error("Factory barcode search failed:", err);
      alert("Search failed. Check console for details.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <h1 className="text-3xl font-semibold mb-4">Factory Barcode Search</h1>

      <div className="mb-4 flex flex-col md:flex-row gap-3 md:items-center">
        <select
          value={field}
          onChange={(e) => setField(e.target.value)}
          className="p-2 border rounded-md"
        >
          <option value="BarcodeNumber">Barcode Number</option>
          <option value="SerialNumber">Serial Number</option>
          <option value="ProductCode">Product Code</option>
          <option value="Program">Program</option>
        </select>

        <input
          type="text"
          placeholder={`Search by ${field}...`}
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") runSearch();
          }}
          className="w-full md:w-1/2 p-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:outline-none"
        />

        <button
          onClick={runSearch}
          disabled={loading}
          className="px-4 py-2 rounded-md bg-primary text-white hover:bg-hover disabled:opacity-50"
        >
          {loading ? "Searching..." : "Search"}
        </button>

        <span className="text-sm text-gray-600">
          {rows.length} instance{rows.length === 1 ? "" : "s"}
        </span>
      </div>

      <div className="mb-4 text-sm text-gray-600">
        Searches parent barcode records first, then loads instances only for
        matching barcodes.
      </div>

      <div className="overflow-x-auto bg-white border border-gray-200 rounded-md">
        <table className="min-w-full">
          <thead className="bg-primary border-b border-gray-200">
            <tr>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Barcode
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Serial Number
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Product Code
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Program
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Qty
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Printed At
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Stretch
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Stretch Time
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Despatch
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Despatch Time
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Hortonwood
              </th>
              <th className="px-4 py-2 text-left text-sm font-medium text-white">
                Hortonwood Time
              </th>
            </tr>
          </thead>

          <tbody className="divide-y divide-gray-100">
            {rows.map((row) => (
              <tr key={`${row.parentId}-${row.id}`} className="hover:bg-gray-50">
                <td className="px-4 py-2 text-sm text-gray-700">
                  {row.BarcodeNumber || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {row.SerialNumber || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {row.ProductCode || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {row.Program || "-"}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {row.Quantity}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {formatDate(row.PrintedAt)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {yesNo(row.stretch_scan)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {formatDate(row.stretch_scantime)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {yesNo(row.despatch_scan)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {formatDate(row.despatch_scantime)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {yesNo(row.hortonwood_scan)}
                </td>
                <td className="px-4 py-2 text-sm text-gray-700">
                  {formatDate(row.hortonwood_scantime)}
                </td>
              </tr>
            ))}

            {!loading && rows.length === 0 && (
              <tr>
                <td colSpan="12" className="px-4 py-6 text-center text-gray-500">
                  No results yet. Search for a barcode, serial number, product
                  code, or program.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}